import axios from "axios";

import store from "../redux/store.ts";
import { setToken, setRefreshToken } from "redux/reducers/authReducer";
import { UIService, UITypes } from "front-lib";

const customAxios = axios.create({
  baseURL: process.env.REACT_APP_BASE_URL,
});

// --- Token refresh logic ---
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

// Request interceptor — attach access token
customAxios.interceptors.request.use(
  function (request) {
    request.headers.Authorization = `Bearer ${store.getState().auth.token}`;
    return request;
  },
  function (error) {
    return Promise.reject(error);
  }
);

// Response interceptor — handle 401 with refresh
customAxios.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (error.response?.status === 401 && !originalRequest._retry) {
      // Skip refresh for the refresh-token endpoint itself
      if (originalRequest.url?.includes("/refresh-token")) {
        store.dispatch(setToken(null));
        store.dispatch(setRefreshToken(null));
        UIService.eventManager.emit(UITypes.AppEventNames.LOGOUT, () => { });
        return Promise.reject(error);
      }

      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((newToken) => {
            originalRequest.headers.Authorization = `Bearer ${newToken}`;
            return customAxios(originalRequest);
          })
          .catch((err) => Promise.reject(err));
      }

      originalRequest._retry = true;
      isRefreshing = true;

      const refreshToken = store.getState().auth.refreshToken;
      if (!refreshToken) {
        isRefreshing = false;
        store.dispatch(setToken(null));
        UIService.eventManager.emit(UITypes.AppEventNames.LOGOUT, () => { });
        return Promise.reject(error);
      }

      try {
        const response = await axios.post(
          `${process.env.REACT_APP_BASE_URL}/refresh-token`,
          { refreshToken }
        );

        const { token: newToken, refreshToken: newRefreshToken } =
          response.data;

        store.dispatch(setToken(newToken));
        store.dispatch(setRefreshToken(newRefreshToken));

        processQueue(null, newToken);

        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        return customAxios(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError, null);
        store.dispatch(setToken(null));
        store.dispatch(setRefreshToken(null));
        UIService.eventManager.emit(UITypes.AppEventNames.LOGOUT, () => { });
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);

// Response interceptor — unwrap response.data
customAxios.interceptors.response.use(
  function (response) {
    return response.data;
  },
  function (error) {
    return Promise.reject(error);
  }
);

export default customAxios;
